import React, { useContext, useCallback, useEffect, useRef, useState } from "react";

import { Card } from './card'
import { gridPresets } from './grid-presets'
import allArticles from "../../content";
import { shuffle } from "../../utils.js";
import context from "../../context";
import { debounce } from "debounce";

const articleCards = shuffle(allArticles);

const Grid = ({ filter, introAnimationComplete, onSetHoleWidth }) => {
	const grid = useRef(null)
  const gridVignetteRef = useRef(null);
	const [ gridConfig, setGridConfig ] = useState(null)
	const [ cardCategory, setCardCategory ] = useState(false)
	const [ visibleCardCategories, setVisibleCardCategories ] = useState([])
	const [ holeWidth, setHoleWidth ] = useState(0)

	const cardRatio = 2350 / 1920
	const animationContext = useContext(context)
	const [ containerPosY, setContainerPosY ] = useState(0)
	const [ containerScale, setContainerScale ] = useState(1)
	const [ filteredArticles, setFilteredArticles ] = useState(articleCards)

	const animationContextRef = useRef(animationContext);
  useEffect(() => {
    animationContextRef.current = animationContext;
  }, [animationContext]);

	let initialising = false

	const gridContainerRef = useCallback(node => {
		// 1. Init grid
    if (node !== null) {
			handleInitialisation()
    }
  }, []);

	const initGrid = (preset, zoom = 1, alignTop = false) => {
		if (initialising) return
		initialising = true
		const containerWidth = document.documentElement.clientWidth
		const rows = Math.ceil(filteredArticles.length / preset.columns);
		const cellPadding = 10;
    const cardHeight = ((containerWidth / preset.columns) - (2 * cellPadding)) * cardRatio + (2 * cellPadding);
		const cardWidth = containerWidth / preset.columns
		const holeWidth = preset.hole.length * (cardWidth * zoom)
		setHoleWidth(holeWidth)
		setGridConfig(prevState => ({
			...prevState,
			containerWidth: containerWidth,
			containerHeight: cardHeight * rows,
			centerPointAdjustmentValue: alignTop ? 0 : getCenterPoint(rows, cardHeight), //neglects any uncomplete last row and calculates the center
			cardWidth: cardWidth,
			cardHeight: cardHeight,
			offsetCol: preset.offsetCol,
			hole: preset.hole,
			columns: preset.columns,
			rows: rows,
			zoom: zoom,
			alignTop: alignTop,
			holeWidth: holeWidth
		}))
		initialising = false
	}

	const getCenterPoint = (rows, cardHeight) => {
		if (rows % 2 === 0) {
			return (rows / 2) * cardHeight
		} else {
			return ((rows - 1) / 2) * cardHeight
		}
	}

	useEffect(() => {
		if (onSetHoleWidth) onSetHoleWidth(holeWidth)
	}, [onSetHoleWidth, holeWidth])

	useEffect(() => {
		//2. Set transform on Grid
		if (gridConfig) setContainerTransform();
	}, [gridConfig])

	const setContainerTransform = (posY = undefined) => {
		setContainerPosY(gridConfig.alignTop ? 0 : `${window.innerHeight / 2 - gridConfig.centerPointAdjustmentValue}px`)
		setContainerScale(gridConfig.zoom)
	}

	const handleInitialisation = () => {
		if (initialising) return
		if (introAnimationComplete) applyGridPresetInStandardView()
		if (!introAnimationComplete) applyGridPresetInIntroView()
	}

	const handleResize = () => {
		handleInitialisation()
	}

	const applyGridPresetInIntroView = () => {
		if (document.documentElement.clientWidth <= 425) {
			initGrid(gridPresets.fourRowsHoleMobile, 1.5)
		} else if (document.documentElement.clientWidth <= 565) {
			initGrid(gridPresets.fourRowsHoleMobile, 1.2)
		} else if (document.documentElement.clientWidth > 565  && document.documentElement.clientWidth <= 767) {
			initGrid(gridPresets.fourRowsHole, 1.3)
		} else if (document.documentElement.clientWidth > 767 && document.documentElement.clientWidth <= 1000) {
			initGrid(gridPresets.fiveRowsHole, 1.2)
		} else {
			initGrid(gridPresets.fiveRowsHole)
		}
	}

	const applyGridPresetInStandardView = () => {
		if (document.documentElement.clientWidth <= 565) {
			initGrid(gridPresets.twoRows, 1, true)
		} else if (document.documentElement.clientWidth > 565  && document.documentElement.clientWidth <= 999) {
			initGrid(gridPresets.threeRows, 1, true)
		} else if (document.documentElement.clientWidth > 999 && document.documentElement.clientWidth <= 1280) {
			initGrid(gridPresets.fourRows, 1, true)
		} else {
			initGrid(gridPresets.fiveRows, 1, true)
		}
	}

	useEffect(() => {
		let resizeHandler = null
		window.addEventListener('resize', resizeHandler = () => debounce(handleResize(), 200), false)
		return () => {
			window.removeEventListener('resize', resizeHandler)
		}
	}, [introAnimationComplete])

	const hoverGroup = (category, direction) => {
		direction === 'leave' ? setCardCategory(false) : setCardCategory(category)
	}

	const filterCards = (article) => {
		if (filter === 'none') return article
		return article.meta.category === filter
	}

	useEffect(() => {
		setFilteredArticles(articleCards.filter(article => filterCards(article)))
	}, [filter])

	useEffect(() => {
		handleInitialisation()
	}, [filteredArticles])

	useEffect(() => {
		if (!animationContext.currentAnimationState) return
		setVisibleCardCategories(prevState => [...prevState, animationContext.currentAnimationState])
		if (introAnimationComplete) handleInitialisation()
	}, [animationContext])

  return (
    <div className="grid" ref={grid}
			style={{
				height: `${gridConfig?.containerHeight}px`,
				marginTop: `${gridConfig?.alignTop ? '115px' : 0}`,
				marginBottom: `${gridConfig?.alignTop ? '115px' : 0}`
			}}>
			<div
				ref={gridContainerRef}
				className={`gridWrapper ${introAnimationComplete ? 'completed' : ''} ${cardCategory ? `hover-${cardCategory}` : ''}`}
				style={{
					height: `${gridConfig?.containerHeight}px`,
					position: `${gridConfig?.alignTop ? 'relative' : 'fixed'}`,
					transform: `translateY(${containerPosY}) scale(${containerScale})`,
					transformOrigin: `center ${gridConfig?.centerPointAdjustmentValue}px`,
					transition: `transform 0.3s ease`,
					marginTop: `${gridConfig?.alignTop ? document.documentElement.clientWidth <= 768 ? 0 : 'calc(115px + 74px)' : 0}`,
				}}
			>
				{gridConfig && filteredArticles.map((card, index) => (
					<Card
						key={`cell-${card.meta.slug}`}
						card={card}
						gridConfig={gridConfig}
						onHoverGroup={hoverGroup}
						index={index + 1}
						visibleCategories={visibleCardCategories}
						introAnimationComplete={introAnimationComplete}
						filter={filter}
					/>
				))}
			</div>
			<div ref={gridVignetteRef} className="gridVignette" style={{opacity: introAnimationComplete ? 0 : 1}}></div>
    </div>
  );
};

export default Grid;
